import './styles.scss';

import React, { useMemo } from 'react';

import Table from '@/common/Table';
import { UserWithMarket } from '@/models/gen/graphql';
import UsersTableHeader from './UsersTableHeader';
import UsersTableRow from './UsersTableRow';

export type UsersTableProps = {
  data: UserWithMarket[];
  selected: string[];
  onSelect: (id: string | string[]) => void;
  onEdit: (userId?: string[]) => void;
  onResetPassword: (user: UserWithMarket) => void;
  onToggleActive: (user: UserWithMarket, isChecked: boolean) => Promise<void>;
  fetchMore?: () => void;
};

/**
 * Users feature table component.
 * Wraps `Table` with a column header and row renderer specific to the User entity.
 * Sorting is handled via Zustand state (useUsersTableState) and drives a BE re-query in the parent page.
 * Search is performed as a BE LIKE query on username, firstName, lastName, and email.
 */
const UsersTable = ({
  data,
  selected,
  onSelect,
  onEdit,
  onResetPassword,
  onToggleActive,
  fetchMore,
}: UsersTableProps): React.JSX.Element => {
  const Header = useMemo(
    (): React.JSX.Element => <UsersTableHeader data={data} selected={selected} onSelect={onSelect} />,
    [data, selected, onSelect]
  );

  return (
    <Table className="UsersTable" header={Header} showControls onEnd={fetchMore}>
      {data.map(
        (row: UserWithMarket): React.JSX.Element => (
          <UsersTableRow
            key={row.id}
            data={row}
            isSelected={selected.includes(row.id)}
            onSelect={(id): void => {
              onSelect(id);
            }}
            onDoubleClick={(): void => {
              onSelect([row.id]);
              onEdit([row.id]);
            }}
            onResetPassword={onResetPassword}
            onToggleActive={onToggleActive}
          />
        )
      )}
    </Table>
  );
};

export default React.memo(UsersTable);
